import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Zap, ShieldCheck, Layers, Check, ArrowRight, CheckCircle, MessageSquare, FileSignature } from '../components/Icons';
import WaitlistModal from '../components/WaitlistModal';
import dashboardImg from '../assets/dashboard_fen.png';
import analysisImg from '../assets/scope_analysis_fen.png';
import knowledgeImg from '../assets/scope_section_fen.png';
import ledgerImg from '../assets/scope_ledger_fen.png';
import healthImg from '../assets/sow_health_fen.png'
import './Landing.css';
import '../components/WaitlistModal.css';
import ScopeCreepCalculator from '../components/ScopeCreepCalculator';

const FEATURES = [
  {
    icon: Zap,
    title: 'Dual-Mode Scope Analysis',
    desc: 'Paste a client message directly or describe the request in your own words — both work equally well. Get a verdict with the exact SOW clause in under 5 seconds.',
  },
  {
    icon: ShieldCheck,
    title: 'Ready-to-Send Replies',
    desc: 'Get two professionally drafted responses — a firm decline and a goodwill offer — in your chosen tone. Regenerate either reply until the wording is exactly right.',
  },
  {
    icon: Layers,
    title: 'Smart PDF Extraction',
    desc: 'Upload your SOW contract PDF and our AI automatically extracts deliverables, exclusions, and timelines into structured sections — no manual tagging required.',
  },
  {
    icon: CheckCircle,
    title: 'Scope Ledger (Pro & Teams)',
    desc: 'Every out-of-scope request is auto-logged with a clean summary. Track change orders from drafted to approved, see your unbilled drift, and turn goodwill work into invoiced revenue.',
  },
  {
    icon: FileSignature,
    title: 'Branded Change Order PDFs',
    desc: 'Convert any agreed scope change into a numbered, professional Change Order PDF in 60 seconds — complete with your logo, parties, pricing, and signature blocks. No templates, no Word docs.',
  },
];

// ── Pricing plans ─────────────────────────────────────────────────────────────
// `waitlist: true` = show "Join Waitlist" painted-door instead of routing
const PLANS = [
  {
    name: 'Starter',
    price: '$0',
    period: 'Free forever',
    highlight: false,
    waitlist: false,
    features: [
      '1 active project',
      'Up to 10 AI scope checks / month',
      'Standard pushback replies',
      'SOW Health Report (contract risks & gaps)',
      'Zero-retention AI (fully private)',
    ],
    cta: 'Start for free',
    ctaStyle: 'secondary',
  },
  {
    name: 'Pro',
    price: '$19',
    period: '/ month · $159 / year',
    highlight: true,
    badge: 'Most popular',
    waitlist: true,
    features: [
      '5 active projects',
      'Unlimited AI scope checks',
      'Custom pushback tones & regeneration',
      'Scope Ledger with change order tracking',
      'Branded Change Order PDF generation',
      'SOW clause citations in AI replies',
      'Impact assessment (hours & cost)',
      'Priority support',
    ],
    cta: 'Get Pro',
    ctaStyle: 'primary',
  },
  {
    name: 'Small Teams',
    price: '$49',
    period: '/ month · $399 / year',
    highlight: false,
    waitlist: true,
    features: [
      'Unlimited active projects',
      'Unlimited AI scope checks',
      'Custom pushback tones & regeneration',
      'Scope Ledger with change order tracking',
      'Branded Change Order PDF generation',
      'SOW clause citations in AI replies',
      'Impact assessment (hours & cost)',
      'Priority support & Team Hub',
    ],
    cta: 'Get Teams',
    ctaStyle: 'secondary',
  },
];

const FAQS = [
  {
    q: 'What is scope creep and why does it cost freelancers so much?',
    a: 'Scope creep is when a client requests work that falls outside the original project agreement defined in your Statement of Work (SOW) contract. It costs freelancers because these extra requests — "can you just add one more page?" or "can we redo the logo too?" — accumulate without being invoiced. Research by AND CO found 71% of freelancers face scope creep on most projects, and 63% never charge for the extra work, averaging over $9,000 in annual lost income per freelancer.',
  },
  {
    q: 'How does Fenscope detect scope creep?',
    a: 'Fenscope evaluates each client request against the deliverables, exclusions, and timelines extracted from your uploaded SOW contract PDF. Paste the client\'s exact message or describe the request in your own words — our AI checks it against your specific contract clauses and returns an in-scope or out-of-scope verdict in under 5 seconds, along with the exact clause that applies and two professionally drafted pushback replies.',
  },
  {
    q: 'What is the Scope Ledger?',
    a: 'The Scope Ledger (Pro/Teams) is a living timeline for each client project. Every out-of-scope request is automatically logged with its estimated cost, hours, and risk level. You can track each item from drafted to client-approved, see your total unbilled drift at a glance, and turn accumulated goodwill work into invoiced revenue through formal change orders.',
  },
  {
    q: 'How do Change Order PDFs work?',
    a: 'Once an out-of-scope addition is agreed upon, click "Convert to CO" on any flagged Scope Ledger item. A guided 3-step wizard walks you through pricing (fixed fee or hourly rate), client details, and payment terms. Preview the complete document in full — then generate a numbered, branded Change Order PDF in one click, ready to send for signature. No Word templates, no formatting work.',
  },
  {
    q: 'How is Fenscope different from other freelance tools?',
    a: 'Most freelance tools handle invoicing or project management after work is done. Fenscope sits at the most critical moment — the instant a client requests something extra. It reads your specific SOW contract, evaluates the request against it, gives you a verdict with a contract citation, drafts your response, and logs the interaction automatically. No other tool closes this gap between client communication and contract enforcement.',
  },
  {
    q: 'Is my SOW contract data private?',
    a: 'Yes. Fenscope uses a zero-retention model — your contract content and client request data are processed for the evaluation and discarded. Your data is never used to train AI models. All stored data uses row-level security, meaning each user can only access their own projects and contracts.',
  },
  {
    q: 'Do I need to install anything?',
    a: 'No. Fenscope is a fully web-based application that runs in any modern browser. There are no extensions to install, no software to download, and no complex setup required. Sign in, create a project, upload your SOW contract PDF, and start checking client requests immediately.',
  },
];

const SCREENSHOTS = [
  { label: 'Project Dashboard', src: dashboardImg, desc: 'All your active client projects in one clean view — with scope status and evaluation count.' },
  { label: 'SOW Health Check', src: healthImg, desc: 'Instantly see how airtight your SOW is — flags missing protections and loopholes before they become disputes.' },
  { label: 'Scope Evaluation', src: analysisImg, desc: 'Paste any client message and get an instant in/out-of-scope verdict with a ready-to-send reply.' },
  { label: 'Scope Sections', src: knowledgeImg, desc: 'Extracted deliverables, exclusions, and timelines from your PDF — all in one organized view.' },
  { label: 'Scope Ledger', src: ledgerImg, desc: 'Every change order, every agreed addition, every unbilled request — logged automatically.' },
];

// Reusable hero demo card
function HeroCard() {
  return (
    <div className="hero-card">
      <div className="hero-card-label">Client request</div>
      <div className="hero-card-text">
        &ldquo;Can you also build the mobile app version?&rdquo;
      </div>
      <div className="hero-card-verdict out">
        <span className="verdict-dot" />
        Out of scope
      </div>
      <div className="hero-card-reply">
        &ldquo;That&rsquo;s outside our agreed deliverables. I&rsquo;d be happy to discuss a separate quote &mdash; my rate is $85/hr.&rdquo;
      </div>
    </div>
  );
}

// ── Video Player (nugget.com style) ───────────────────────────────────────────────
function VideoPlayer() {
  const [playing, setPlaying] = React.useState(false);
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    if (playing && videoRef.current) {
      videoRef.current.play().catch(() => { });
    }
  }, [playing]);

  return (
    <div className={`vp-wrap${playing ? ' vp-playing' : ''}`}>
      {!playing && (
        <button className="vp-thumb" onClick={() => setPlaying(true)} aria-label="Play Fenscope demo">
          <img src={dashboardImg} alt="Fenscope app preview" className="vp-poster" />
          <div className="vp-overlay">
            <div className="vp-play-btn">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
            <span className="vp-duration">2-minute demo</span>
          </div>
        </button>
      )}
      {playing && (
        <video
          ref={videoRef}
          src="/fenscope_demp.mp4"
          controls
          autoPlay
          playsInline
          className="vp-video vp-video-visible"
          onEnded={() => setPlaying(false)}
        />
      )}
    </div>
  );
}

// ── Feature Spotlight (two premium features) ────────────────────────────────────
function FeatureSpotlight({ onLogin }) {
  return (
    <section className="spotlight-section">
      <div className="section-inner">
        <p className="section-eyebrow">What sets Fenscope apart</p>
        <h2 className="section-heading">Two features you&apos;ll<br />rely on every single day.</h2>
        <div className="spotlight-grid">

          {/* Card 1 — Contract Health */}
          <div className="spotlight-card spotlight-card-dark">
            <div className="spotlight-card-top">
              <div className="spotlight-icon-wrap spotlight-icon-shield">
                <ShieldCheck size={22} />
              </div>
              <span className="spotlight-tag">Every plan · New</span>
            </div>
            <h3 className="spotlight-card-title">SOW Health &amp; Risk Report</h3>
            <p className="spotlight-card-desc">
              When you upload a contract PDF, Fenscope scores it against 10 industry standards —
              surfacing weak clauses, missing protections, and exact language to add before anything goes wrong.
            </p>
            <ul className="spotlight-bullets">
              <li><span className="spl-dot spl-dot-red" />Pinpoints high-risk gaps: no kill fee, vague deliverables, missing revision limits</li>
              <li><span className="spl-dot spl-dot-green" />Lists all protective clauses already present in your contract</li>
              <li><span className="spl-dot spl-dot-blue" />Gives you exact clause language to add — copy and paste ready</li>
            </ul>
          </div>

          {/* Card 2 — Flexible Input */}
          <div className="spotlight-card spotlight-card-light">
            <div className="spotlight-card-top">
              <div className="spotlight-icon-wrap spotlight-icon-msg">
                <MessageSquare size={22} />
              </div>
              <span className="spotlight-tag spotlight-tag-accent">Scope Analysis · Smarter</span>
            </div>
            <h3 className="spotlight-card-title">Context-Aware Evaluation</h3>
            <p className="spotlight-card-desc">
              Most tools only accept copy-paste. Fenscope understands context — paste the client&apos;s
              exact message or simply describe what they&apos;re asking. Both get the same precision verdict.
            </p>
            <div className="spotlight-examples">
              <div className="spotlight-example">
                <span className="spe-tag">Exact message</span>
                <span className="spe-text">&ldquo;Can you also build a mobile app version?&rdquo;</span>
              </div>
              <div className="spe-or">or</div>
              <div className="spotlight-example spotlight-example-b">
                <span className="spe-tag">Your own words</span>
                <span className="spe-text">&ldquo;Client is asking for a mobile app&rdquo;</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

// ── Scope Creep Impact Section ─────────────────────────────────────────────────
const IMPACT_STATS = [
  {
    value: '71%',
    label: 'of freelancers deal with scope creep on most projects',
    sub: 'AND CO Freelancer Report, 2019',
    cite: 'AND CO (now Cushion) 2019 Freelancer Survey — conducted across 1,000+ independent professionals. 71% reported scope creep as a recurring challenge on project-based work.',
  },
  {
    value: '63%',
    label: 'never invoice for the out-of-scope work they do',
    sub: 'Freelancing in America, Upwork × Freelancers Union, 2019',
    cite: 'Freelancing in America 2019 (Upwork × Freelancers Union) — the largest annual study of US freelancers. 63% of respondents reported not pursuing compensation for work done outside the original agreement.',
  },
  {
    value: '$9,000+',
    label: 'average annual loss from unbilled out-of-scope work',
    sub: 'est. based on BLS median rate × avg. extra hours',
    cite: 'Conservative derivation: US median independent contractor rate $45–55/hr (Bureau of Labor Statistics, 2023 OES survey) × avg. 15 unbilled hrs/month from scope additions = $8,100–11,000/yr. Consistent with self-reported data in the Freelancing in America study.',
  },
];

function ImpactSection() {
  return (
    <section className="impact-section">
      <div className="section-inner">
        <p className="section-eyebrow impact-eyebrow">The hidden cost</p>
        <h2 className="impact-heading">
          One &ldquo;small ask&rdquo; just<br />
          cost you $1,400.
        </h2>
        <div className="impact-story">
          <blockquote className="impact-quote">
            &ldquo;You finish the logo. The client says:
            <em> &lsquo;Can you also do the brand guidelines?&rsquo;</em>
            {' '}You say yes &mdash; it&rsquo;s just 2 more hours. Then comes the social kit. Then the pitch deck.
            You&rsquo;ve worked 14 extra hours and invoiced for zero.&rdquo;
          </blockquote>
          <p className="impact-story-sub">
            This is scope creep. It happens to every designer, developer, writer, and consultant &mdash;
            and most never bill for it. Not because they don&rsquo;t want to.
            Because they don&rsquo;t have the words.
          </p>
        </div>
        <div className="impact-stats">
          {IMPACT_STATS.map((s, i) => (
            <div key={i} className="impact-stat">
              <div className="impact-stat-value">{s.value}</div>
              <div className="impact-stat-label">{s.label}</div>
              {s.cite ? (
                <div className="impact-stat-sub impact-stat-cite" title={s.cite}>
                  {s.sub} <span className="impact-cite-mark">?</span>
                </div>
              ) : (
                <div className="impact-stat-sub">{s.sub}</div>
              )}
            </div>
          ))}
        </div>
        <p className="impact-bottom-line">
          Fenscope spots it <strong>before you say yes</strong> &mdash; and gives you the exact words to respond.
        </p>
      </div>
    </section>
  );
}

// ── Toast ──────────────────────────────────────────────────────────────────────
function Toast({ message, onDone }) {
  React.useEffect(() => {
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="wl-toast" role="status" aria-live="polite">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
      {message}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate();

  // Waitlist modal state
  const [waitlistPlan, setWaitlistPlan] = useState(null); // null = closed
  const [toastMessage, setToastMessage] = useState('');

  function goLogin() { navigate('/login'); }

  const openWaitlist = useCallback((planName) => setWaitlistPlan(planName), []);
  const closeWaitlist = useCallback(() => setWaitlistPlan(null), []);
  const handleWaitlistSuccess = useCallback(() => {
    setToastMessage("You're on the list! Keep an eye on your inbox.");
  }, []);

  // Handler per card
  function handlePlanCta(plan) {
    if (plan.waitlist) {
      openWaitlist(plan.name);
    } else {
      goLogin();
    }
  }

  return (
    <div className="landing-page">

      {/* ── Waitlist modal ────────────────────────── */}
      <WaitlistModal
        isOpen={waitlistPlan !== null}
        plan={waitlistPlan}
        onClose={closeWaitlist}
        onSuccess={handleWaitlistSuccess}
      />

      {/* ── Success toast ─────────────────────────── */}
      {toastMessage && (
        <Toast message={toastMessage} onDone={() => setToastMessage('')} />
      )}

      {/* ── Nav ─────────────────────────────────── */}
      <nav className="landing-nav">
        <div className="landing-logo">
          <span className="brand-logo-text">fenscope.</span>
        </div>
        <div className="landing-nav-actions">
          <button className="btn btn-secondary" onClick={goLogin}>Log in</button>
          <button className="btn btn-primary" onClick={goLogin}>Start free</button>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────── */}
      <section className="hero">
        <div className="hero-inner">
          {/* <div className="hero-badge">
            <Zap size={13} />
            No extension needed — works in your browser
          </div> */}
          <h1 className="hero-title">
            Stop giving away<br />
            <span className="hero-title-accent">free work.</span>
          </h1>
          {/* Answer-first block: 40–60 words for AI snippet extraction (GEO) */}
          <p className="hero-answer-first">
            Fenscope is an AI scope creep detection tool for freelancers. Upload your Statement of Work (SOW) contract, check any client request in under 5 seconds, and get an instant in/out-of-scope verdict, a professional pushback reply, and a branded change order PDF — so every hour you work gets billed.
          </p>
          <div className="hero-actions">
            <button className="btn btn-primary btn-lg" onClick={goLogin}>
              Try it for free <ArrowRight size={16} />
            </button>
          </div>
        </div>
        {/* Desktop card */}
        <div className="hero-visual">
          <HeroCard />
        </div>
      </section>

      {/* Mobile card (appears below hero text on small screens) */}
      <div className="hero-card-mobile-wrap">
        <div className="section-inner">
          <HeroCard />
        </div>
      </div>

      {/* ── Scope Creep Cost Calculator ──────────── */}
      <ScopeCreepCalculator />

      {/* ── Scope Creep Impact ──────────────────── */}
      <ImpactSection />

      {/* ── Video Demo ──────────────────────────── */}
      <section className="video-section">
        <div className="section-inner">
          <p className="section-eyebrow">See it in action</p>
          <h2 className="section-heading">From client message<br />to perfect reply in seconds</h2>
          <p className="section-sub">Watch how Fenscope evaluates scope creep and generates a professional response without you lifting a finger.</p>
          <div className="video-player-wrap">
            <VideoPlayer />
          </div>
        </div>
      </section>

      {/* ── Screenshots ─────────────────────────── */}
      <section className="screenshots-section">
        <div className="section-inner">
          <p className="section-eyebrow">Inside the app</p>
          <h2 className="section-heading">Precision scope control,<br />built for high-stakes projects.</h2>
        </div>
        <div className="screenshots-scroll">
          {SCREENSHOTS.map((shot, i) => (
            <div key={i} className="screenshot-frame">
              <img
                src={shot.src}
                alt={shot.label}
                className="screenshot-img"
                loading="lazy"
              />
              <div className="screenshot-meta">
                <div className="screenshot-label">{shot.label}</div>
                <p className="screenshot-desc">{shot.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Feature Spotlight ────────────────────── */}
      <FeatureSpotlight onLogin={goLogin} />

      {/* ── Features ────────────────────────── */}
      <section className="features-section">
        <div className="section-inner">
          <p className="section-eyebrow">How it works</p>
          <h2 className="section-heading">Everything you need to<br />protect your scope</h2>
          <div className="features-grid">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="feature-card">
                <div className="feature-icon"><Icon size={20} /></div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────── */}
      <section className="pricing-section">
        <div className="section-inner">
          <p className="section-eyebrow">Pricing</p>
          <h2 className="section-heading">Pricing that pays for itself</h2>
          <p className="section-sub">Scale your freelance business without missing a single billable hour.</p>
          <div className="pricing-grid">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`pricing-card ${plan.highlight ? 'pricing-card-highlight' : ''}`}
              >
                <div className="pricing-card-top">
                  <div className="pricing-plan-row">
                    <span className="pricing-plan-name">{plan.name}</span>
                    {plan.badge && <span className="pricing-badge">{plan.badge}</span>}
                  </div>
                  <div className="pricing-price-row">
                    <span className="pricing-price">{plan.price}</span>
                    <span className="pricing-period">{plan.period}</span>
                  </div>
                </div>

                <ul className="pricing-features">
                  {plan.features.map((f) => (
                    <li key={f}>
                      <Check size={14} />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* CTA — waitlist plans show a slightly different label hint */}
                <button
                  className={`btn btn-lg pricing-cta ${plan.ctaStyle === 'primary' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handlePlanCta(plan)}
                >
                  {plan.waitlist ? (
                    <>
                      {plan.cta}
                      <span className="pricing-cta-badge">Early access</span>
                    </>
                  ) : plan.cta}
                </button>

              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────── */}
      <section className="faq-section">
        <div className="section-inner section-inner-narrow">
          <p className="section-eyebrow">FAQ</p>
          <h2 className="section-heading">Common questions</h2>
          <div className="faq-list">
            {FAQS.map(({ q, a }) => (
              <div key={q} className="faq-item">
                <h3>{q}</h3>
                <p>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ──────────────────────────── */}
      <section className="cta-section">
        <div className="section-inner">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="cta-title">Ready to reclaim your time?</h2>
              <p className="cta-text">Stop letting 'one quick change' eat into your profits. Start setting boundaries in under 30 seconds.</p>
              <div className="cta-actions-wrap">
                <button className="btn btn-primary btn-lg cta-main-btn" onClick={goLogin}>
                  Get Started for Free <ArrowRight size={16} />
                </button>
                <p className="cta-hint">No credit card required &bull; Instant setup</p>
              </div>
            </div>
            <div className="cta-glow-1"></div>
            <div className="cta-glow-2"></div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="section-inner footer-inner">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span className="brand-logo-text" style={{ fontSize: '1.1rem' }}>fenscope.</span>
            <p>&copy; 2026 Fenscope. Protect your scope, protect your income.</p>
            {/* Freshness signal for AI crawlers — update this date when content changes */}
            <time dateTime="2026-04-26" style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              Last updated April 2026
            </time>
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '0.8rem' }}>
            <Link to="/privacy" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Privacy Policy</Link>
            <Link to="/terms" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Terms &amp; Conditions</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
